import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Linkedin, X } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal, Kicker } from "../components/Reveal";
import { TEAM } from "../data/content";
import { TEAM_PHOTOS } from "../data/photos";
import { ElmMark } from "../components/Logo";

export const Portrait = ({ id, name, className = "" }) => {
  const src = TEAM_PHOTOS[id];
  if (!src) {
    return (
      <div
        className={`flex flex-col items-center justify-center gap-2 border border-dashed border-[#0A2B3D]/30 bg-[#EBF3F8] ${className}`}
        data-testid={`portrait-placeholder-${id}`}
      >
        <ElmMark className="w-10 h-10 opacity-40" />
        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#0A2B3D]/50">Portrait forthcoming</span>
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={`${name}, American Elm Advisors`}
      loading="lazy"
      className={`object-cover object-top ${className}`}
      data-testid={`portrait-${id}`}
    />
  );
};

const TEAM_ORDER = ["michael-ponticello", "jessica-murray", "eulalie-paris", "dennis-beirne", "robert-aufenanger", "julie-bruno"];

const WhoWeAre = () => {
  const [active, setActive] = useState(null);
  const orderedTeam = TEAM_ORDER.map((id) => TEAM.find((m) => m.id === id)).filter(Boolean);

  return (
    <div data-testid="who-we-are-page">
      <PageHero
        kicker="Who We Are"
        lines={[<span key="a">Practitioners,</span>, <span key="b">not <em className="italic text-[#3E9BDC]">observers.</em></span>]}
        intro="American Elm Advisors offers Compliance, Capital Markets, and Trust Services consulting. We seek to empower our partners — enabling you to reach your fullest potential by seamlessly integrating critical operations services, with a customized approach that assesses your goals and supports the areas you need most."
        meta={["Portsmouth, NH", "Est. by Industry Veterans", "Affiliate: American Elm Distribution Partners"]}
      />

      <section className="py-20 md:py-28" data-testid="team-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12">
          <Reveal>
            <Kicker>Our Team</Kicker>
            <h2 className="mt-5 font-serif font-bold text-3xl sm:text-4xl text-[#0A2B3D] tracking-tight max-w-2xl leading-[1.15]">
              Decades of seat-level experience, at your side.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {orderedTeam.map((m, i) => (
              <Reveal key={m.id} delay={(i % 3) * 0.07}>
                <button
                  onClick={() => setActive(m)}
                  data-testid={`team-card-${m.id}`}
                  className="w-full text-left h-full bg-white border border-[#0A2B3D]/12 hover:border-[#0A2B3D]/40 hover:shadow-xl transition-all duration-500 group overflow-hidden"
                >
                  <div className="p-7 flex items-start gap-5">
                    <Portrait
                      id={m.id}
                      name={m.name}
                      className="w-16 h-16 rounded-full grayscale group-hover:grayscale-0 transition-all duration-500 shrink-0"
                    />
                    <div className="min-w-0">
                      <h3 className="font-serif font-bold text-xl text-[#0A2B3D]">{m.name}</h3>
                      <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.15em] text-[#3E9BDC]">{m.title}</p>
                      <p className="mt-3 text-sm text-[#475569]">{m.focus}</p>
                      {m.linkedin && (
                        <a
                          href={m.linkedin}
                          target="_blank"
                          rel="noreferrer"
                          aria-label={`View ${m.name} on LinkedIn`}
                          data-testid={`linkedin-link-${m.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-[#3E9BDC] hover:text-[#0A2B3D] transition-colors"
                        >
                          <Linkedin size={14} /> View LinkedIn Profile
                        </a>
                      )}
                      <div>
                        <span className="mt-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#0A2B3D] group-hover:text-[#3E9BDC] transition-colors">
                          Read Biography <ArrowRight size={13} />
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-[#082838]/80 backdrop-blur-sm p-0 sm:p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActive(null)}
            data-testid="bio-modal-overlay"
          >
            <motion.div
              className="bg-white w-full max-w-4xl max-h-[88vh] overflow-y-auto relative"
              data-lenis-prevent
              data-testid="bio-modal"
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setActive(null)}
                data-testid="bio-modal-close"
                className="absolute top-5 right-5 z-10 p-2 bg-white/90 text-[#0A2B3D] hover:text-[#3E9BDC] transition-colors"
                aria-label="Close biography"
              >
                <X size={22} />
              </button>
              <div className="grid grid-cols-1 md:grid-cols-5">
                <div className="md:col-span-2 bg-[#EBF3F8] p-8 md:p-10 flex items-start justify-center">
                  <img
                    src={TEAM_PHOTOS[active.id]}
                    alt={`${active.name}, American Elm Advisors`}
                    className="w-full max-w-[240px] h-auto border-4 border-white shadow-xl"
                    data-testid={`modal-portrait-${active.id}`}
                  />
                </div>
                <div className="md:col-span-3 p-8 md:p-12">
                  <h3 className="font-serif font-bold text-3xl text-[#0A2B3D]">{active.name}</h3>
                  <p className="mt-1 font-mono text-xs uppercase tracking-[0.18em] text-[#3E9BDC]">{active.title}</p>
                  <p className="mt-2 text-sm text-[#475569]">{active.focus}</p>
                  {active.linkedin && (
                    <a
                      href={active.linkedin}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`View ${active.name} on LinkedIn`}
                      data-testid={`modal-linkedin-${active.id}`}
                      className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-[#3E9BDC] hover:text-[#0A2B3D] transition-colors"
                    >
                      <Linkedin size={14} /> View LinkedIn Profile
                    </a>
                  )}
                  <div className="mt-6 space-y-4">
                    {active.bio.map((p, i) => {
                      if (!active.links) {
                        return (
                          <p key={p} className="text-[#475569] leading-relaxed text-[15px]">{p}</p>
                        );
                      }
                      let remaining = p;
                      const parts = [];
                      active.links.forEach((l) => {
                        const idx = remaining.indexOf(l.text);
                        if (idx > -1) {
                          parts.push(remaining.slice(0, idx));
                          parts.push(
                            <a
                              key={l.text}
                              href={l.url}
                              target="_blank"
                              rel="noreferrer"
                              aria-label={`Visit ${l.text}`}
                              data-testid="bio-link-new-futures"
                              className="text-[#3E9BDC] underline underline-offset-2 hover:text-[#0A2B3D] transition-colors"
                            >
                              {l.text}
                            </a>
                          );
                          remaining = remaining.slice(idx + l.text.length);
                        }
                      });
                      parts.push(remaining);
                      return (
                        <p key={p} className="text-[#475569] leading-relaxed text-[15px]">{parts}</p>
                      );
                    })}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <section className="bg-[#EBF3F8] py-20 md:py-28" data-testid="firm-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-7">
            <Kicker>The Firm</Kicker>
            <h2 className="mt-5 font-serif font-bold text-3xl sm:text-4xl text-[#0A2B3D] tracking-tight leading-[1.15]">
              Built by operators who have carried the responsibility themselves.
            </h2>
            <p className="mt-6 text-[#475569] leading-relaxed">
              American Elm Advisors was founded to provide expert consulting in compliance, investor
              relations, and capital markets — recognizing a growing need for specialized guidance in
              navigating complex regulatory landscapes and optimizing investor engagement. The firm
              was established with a mission to offer tailored, results-driven solutions that help
              clients streamline compliance processes, build stronger investor relationships, and
              position themselves effectively in the capital markets.
            </p>
            <p className="mt-4 text-[#475569] leading-relaxed">
              In conjunction with our affiliate, American Elm Distribution Partners, we stand as a
              leading managing broker-dealer and versatile multi-product platform.
            </p>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.12}>
            <div className="bg-[#0A2B3D] text-white p-10 md:p-12 h-full" data-testid="hq-card">
              <p className="eyebrow text-[#A9CFE2]">Headquarters</p>
              <p className="mt-5 font-serif text-2xl leading-snug">
                104 Congress Street, Suite 404<br />Portsmouth, NH 03801
              </p>
              <div className="mt-8 pt-6 border-t border-white/10">
                <Link to="/contact" data-testid="firm-contact-button" className="btn-gold">
                  Contact the Firm <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default WhoWeAre;
